// Appointments service layer for admin operations
import { supabase } from '@/integrations/supabase/client';

export interface Appointment {
  id: string;
  booking_id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string | null;
  date_of_birth: string;
  employer_title: string | null;
  service: string;
  situation: string;
  appointment_type: string | null;
  appointment_start: string | null;
  appointment_end: string | null;
  language: string | null;
  payment_method: string | null;
  payment_status: string | null;
  payment_amount: number | null;
  payment_currency: string | null;
  payment_source: 'online' | 'offline' | null;
  payment_notes: string | null;
  payment_date: string | null;
  stripe_session_id: string | null;
  calendar_event_id: string | null;
  zoom_meeting_id: string | null;
  zoom_join_url: string | null;
  zoom_password: string | null;
  zoom_start_url: string | null;
  referred_by: string | null;
  referred_other: string | null;
  consent_notifications: boolean | null;
  consent_non_refundable: boolean | null;
  consent_ip: string | null;
  signature_typed_name: string | null;
  created_at: string;
  updated_at: string;
}

export type AppointmentStatus = 'pending' | 'paid' | 'confirmed' | 'cancelled' | 'completed' | 'failed' | 'test';

export interface GetAppointmentsParams {
  from?: string;
  to?: string;
  status?: string;
  search?: string;
  service?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetAppointmentsResult {
  data: Appointment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Get appointments with filters, pagination, and sorting
export async function getAppointments(params: GetAppointmentsParams = {}): Promise<GetAppointmentsResult> {
  const {
    from,
    to,
    status,
    search,
    service,
    page = 1,
    pageSize = 20,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = params;

  let query = supabase
    .from('bookings')
    .select('*', { count: 'exact' });

  // Apply date range filter
  if (from) {
    query = query.gte('appointment_start', from);
  }
  if (to) {
    query = query.lte('appointment_start', to);
  }

  // Apply status filter
  if (status && status !== 'all') {
    query = query.eq('payment_status', status);
  }

  // Apply service filter
  if (service && service !== 'all') {
    query = query.eq('service', service);
  }

  // Apply search filter (name, email, phone, booking_id)
  if (search) {
    query = query.or(
      `full_name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%,booking_id.ilike.%${search}%`
    );
  }

  // Apply sorting
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }

  return {
    data: (data || []) as Appointment[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
}

// Get single appointment by ID
export async function getAppointmentById(id: string): Promise<Appointment | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching appointment:', error);
    throw error;
  }

  return data as Appointment;
}

// Update appointment status
export async function updateAppointmentStatus(
  id: string,
  status: AppointmentStatus
): Promise<Appointment> {
  const { data, error } = await supabase
    .from('bookings')
    .update({ payment_status: status, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating appointment status:', error);
    throw error;
  }

  return data as Appointment;
}

// Create manual appointment
export async function createManualAppointment(appointmentData: {
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  service: string;
  situation: string;
  appointment_type?: string;
  appointment_start?: string;
  appointment_end?: string;
  language?: string;
  payment_status?: string;
  notes?: string;
}): Promise<Appointment> {
  const bookingId = `ADMIN-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  
  const { data, error } = await supabase
    .from('bookings')
    .insert({
      booking_id: bookingId,
      full_name: appointmentData.full_name,
      email: appointmentData.email,
      phone: appointmentData.phone,
      date_of_birth: appointmentData.date_of_birth,
      service: appointmentData.service,
      situation: appointmentData.situation || 'Manual booking created by admin',
      appointment_type: appointmentData.appointment_type || 'In-person',
      appointment_start: appointmentData.appointment_start,
      appointment_end: appointmentData.appointment_end,
      language: appointmentData.language || 'en',
      payment_status: appointmentData.payment_status || 'confirmed',
      payment_method: 'Manual Entry',
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating manual appointment:', error);
    throw error;
  }

  return data as Appointment;
}

// Update appointment calendar event ID
export async function updateAppointmentCalendarEventId(
  id: string,
  calendarEventId: string
): Promise<Appointment> {
  const { data, error } = await supabase
    .from('bookings')
    .update({ 
      calendar_event_id: calendarEventId, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating calendar event ID:', error);
    throw error;
  }

  return data as Appointment;
}

// Get appointment stats
export async function getAppointmentStats() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);

  // Get today's appointments
  const { count: todayCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .gte('appointment_start', today.toISOString())
    .lt('appointment_start', tomorrow.toISOString());

  // Get this week's appointments
  const { count: weekCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .gte('appointment_start', weekStart.toISOString())
    .lt('appointment_start', weekEnd.toISOString());

  // Get pending appointments
  const { count: pendingCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .eq('payment_status', 'pending');

  // Get confirmed/paid appointments
  const { count: confirmedCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .in('payment_status', ['paid', 'confirmed']);

  return {
    today: todayCount || 0,
    thisWeek: weekCount || 0,
    pending: pendingCount || 0,
    confirmed: confirmedCount || 0,
  };
}

// Service types available
export const SERVICE_TYPES = [
  'Immigration',
  'Criminal Defense',
  'Personal Injury',
  'Family Law',
  'Tax',
  'Probate & Estate Planning',
  'Real Estate',
  'Business Law',
];

// Appointment types
export const APPOINTMENT_TYPES = ['Video', 'Phone', 'In-person'];

// Status options
export const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-800' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
  { value: 'test', label: 'Test', color: 'bg-purple-100 text-purple-800' },
];
